import React, { useState, useEffect } from 'react';
import { Modal, Input, TreeSelect, Button, Row, Form } from 'antd';
import PropTypes from 'prop-types';

import { useContentLibrary } from 'context/ContentLibraryContext';
import { handleModalTreeSearch } from 'helpers/contentlibrary';
import CloseIcon from 'components/Icons/CloseIcon';
import DropdownIcon from 'components/Icons/DropdownIcon';
import AddFolder from 'components/Icons/AddFolder';
import FolderIcon from 'components/Icons/FolderIcon';

import './SaveDeliverablesModal.scss';

/**
 * Save deliverables modal
 *
 * @param {Object} props
 * @returns {React.ReactNode}
 */
const SaveDeliverablesModal = ({
  userId,
  showSaveDeliverableModal,
  setShowSaveDeliverableModal,
  saveDeliverable,
  selectedDeliverable,
  setSelectedDeliverable,
  addChild,
}) => {
  const { initialTreeData, setIndexes, filterTreeData, treeData, addKey } = useContentLibrary();
  const filteredTreeData = filterTreeData(treeData, 'catalogs') || { children: [] };

  const [deliverableNameValue, setDeliverableNameValue] = useState(selectedDeliverable?.name);
  const [selectedFolder, setSelectedFolder] = useState('');
  const [modalTreeData, setModalTreeData] = useState(filteredTreeData);

  const [form] = Form.useForm();

  // Update modal tree data when filtered tree data changes
  useEffect(() => {
    setModalTreeData(filteredTreeData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeData]);

  /**
   * Handle save deliverable
   */
  const handleSaveDeliverable = () => {
    if (saveDeliverable) {
      let deliverable = selectedDeliverable;
      deliverable.name = deliverableNameValue;
      deliverable.folderKey = selectedFolder;
      saveDeliverable(deliverable, () => {
        setShowSaveDeliverableModal(false);
      });
    }
  };

  /**
   * Handle cancel
   */
  const handleCancel = () => {
    setDeliverableNameValue('');
    setSelectedFolder(null);
    setSelectedDeliverable(null);
    setShowSaveDeliverableModal(false);
  };

  /**
   * Add new child from search
   *
   * @param {string} treeSearchValue
   */
  const addNewChildFromSearch = (treeSearchValue) => {
    let _selectedFolderKey = selectedFolder;

    if (_selectedFolderKey === 'newTemplateFolder') {
      _selectedFolderKey = '';
    }

    addChild({
      variables: {
        child: { title: treeSearchValue?.trim(), uid: userId },
        key: `${_selectedFolderKey}${_selectedFolderKey ? '.' : ''}children`,
        id: treeData._id,
      },
    });
  };

  /**
   * Handle deliverable search
   *
   * @param {string} tempSearchValue
   */
  const handleDeliverableSearch = (tempSearchValue) => {
    const modifiedTreeData = handleModalTreeSearch({
      tempSearchValue,
      treeData: modalTreeData,
      setIndexes,
      initialTreeData,
    });

    modifiedTreeData.key = '';
    addKey(modifiedTreeData, '', 1, filteredTreeData.children?.length || 0);
    modifiedTreeData.children = modifiedTreeData?.children?.reverse();
    setModalTreeData(filterTreeData(modifiedTreeData, 'catalogs'));
  };

  /**
   * Handle deliverable change
   *
   * @param {string} value
   * @param {Object} info
   */
  const handleDeliverableChange = (value, info) => {
    const [newValue] = info;
    const selectedTree = value.split('_');

    // If new folder is being created
    if (value && typeof value === 'string' && value.match(/_new/)) {
      addNewChildFromSearch(newValue);
      setSelectedFolder(selectedTree?.[0] || '');
    } else {
      setSelectedFolder(selectedTree?.[0] || '');
    }
  };

  /**
   * Render tree data
   *
   * @returns {Array}
   */
  const renderTreeData = () => {
    return (modalTreeData?.children || [])
      .filter((item) => item.title !== null)
      .map((item) => {
        return {
          ...item,
          icon: item.create ? <AddFolder /> : <FolderIcon />,
        };
      });
  };

  return (
    <Modal
      visible={showSaveDeliverableModal}
      onCancel={handleCancel}
      footer={null}
      width={480}
      centered
      className="confirm-modal save-deliverable-modal"
      closeIcon={<CloseIcon className="close-icon" />}
      maskStyle={{ zIndex: 1009 }}>
      <div className="save-deliverable-modal-content">
        <h3 className="title">Save Deliverable</h3>
        <div className="divider" />

        <Row className="deliverable-name-row">
          <Form
            layout="vertical"
            form={form}
            name="save-deliverable-form"
            initialValues={{
              deliverableName: deliverableNameValue,
            }}>
            <Form.Item
              label={<span className="catalog-add-product__label">Deliverable name</span>}
              name="deliverableName"
              rules={[
                { required: true, message: 'Please enter product name' },
                { whitespace: true, message: 'Field cannot be only spaces' },
              ]}>
              <Input
                placeholder="Enter deliverable name"
                value={deliverableNameValue}
                onChange={(e) => setDeliverableNameValue(e.target.value)}
              />
            </Form.Item>
          </Form>
        </Row>

        <Row className="select-folder-row">
          <label>Select/create folder</label>
          <TreeSelect
            showSearch
            dropdownRender={(menu) => <div className="select-folder-tree-select">{menu}</div>}
            treeData={renderTreeData()}
            treeIcon={true}
            placeholder="Select/create folder"
            suffixIcon={<DropdownIcon />}
            placement="bottom"
            onChange={handleDeliverableChange}
            onSearch={handleDeliverableSearch}
          />
        </Row>

        <div className="button-wrapper">
          <Button className="large-btn grey-btn" onClick={handleCancel}>
            Cancel
          </Button>

          <Button
            key="back"
            className="large-btn secondary-btn"
            onClick={handleSaveDeliverable}
            disabled={!deliverableNameValue.trim()}>
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
};

SaveDeliverablesModal.propTypes = {
  userId: PropTypes.string,
  treeData: PropTypes.object,
  setShowSaveDeliverableModal: PropTypes.func,
  saveDeliverable: PropTypes.func,
  selectedDeliverable: PropTypes.object,
  setSelectedDeliverable: PropTypes.func,
  addChild: PropTypes.func,
};

export default SaveDeliverablesModal;
